import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"

// Shared lightbox state (module-level, one instance per page)
let lbModal = null
let lbPhotos = []
let lbIndex = 0

function lbShow(photos, index) {
  lbPhotos = photos
  lbIndex = index
  lbUpdate()
  lbModal.show()
}

function lbUpdate() {
  const img = document.getElementById("lbImg")
  const counter = document.getElementById("lbCounter")
  const prev = document.getElementById("lbPrev")
  const next = document.getElementById("lbNext")
  if (img) img.src = lbPhotos[lbIndex]
  if (counter) counter.textContent = lbPhotos.length > 1 ? `${lbIndex + 1} / ${lbPhotos.length}` : ""
  if (prev) prev.style.display = lbPhotos.length > 1 ? "" : "none"
  if (next) next.style.display = lbPhotos.length > 1 ? "" : "none"
}

function lbNext() { lbIndex = (lbIndex + 1) % lbPhotos.length; lbUpdate() }
function lbPrev() { lbIndex = (lbIndex - 1 + lbPhotos.length) % lbPhotos.length; lbUpdate() }

function ensureLightbox() {
  if (lbModal) return
  if (document.getElementById("photoLightbox")) {
    lbModal = Modal.getOrCreateInstance(document.getElementById("photoLightbox"))
    return
  }

  const el = document.createElement("div")
  el.id = "photoLightbox"
  el.className = "modal fade"
  el.setAttribute("tabindex", "-1")
  el.innerHTML = `
    <div class="modal-dialog modal-fullscreen m-0">
      <div class="modal-content border-0" style="background:rgba(0,0,0,0.93);">
        <div class="modal-body d-flex align-items-center justify-content-center p-0 position-relative" style="min-height:100dvh;" id="lbBody">
          <button type="button" class="btn-close btn-close-white position-absolute top-0 end-0 m-3" data-bs-dismiss="modal" style="z-index:10;"></button>
          <button type="button" id="lbPrev" class="btn btn-link text-white position-absolute start-0 top-50 translate-middle-y px-2 px-md-4" style="z-index:10;font-size:2.5rem;line-height:1;opacity:0.8;">&#8249;</button>
          <img id="lbImg" src="" alt="" draggable="false" style="max-height:90dvh;max-width:92vw;object-fit:contain;user-select:none;border-radius:4px;">
          <button type="button" id="lbNext" class="btn btn-link text-white position-absolute end-0 top-50 translate-middle-y px-2 px-md-4" style="z-index:10;font-size:2.5rem;line-height:1;opacity:0.8;">&#8250;</button>
          <div id="lbCounter" class="position-absolute bottom-0 start-50 translate-middle-x text-white-50 small pb-3"></div>
        </div>
      </div>
    </div>
  `
  document.body.appendChild(el)
  lbModal = new Modal(el)

  document.getElementById("lbPrev").addEventListener("click", lbPrev)
  document.getElementById("lbNext").addEventListener("click", lbNext)

  // Touch swipe
  let touchStartX = 0
  const body = el.querySelector("#lbBody")
  body.addEventListener("touchstart", e => { touchStartX = e.changedTouches[0].clientX }, { passive: true })
  body.addEventListener("touchend", e => {
    const dx = e.changedTouches[0].clientX - touchStartX
    if (Math.abs(dx) > 50) dx < 0 ? lbNext() : lbPrev()
  }, { passive: true })

  // Keyboard
  document.addEventListener("keydown", e => {
    if (!document.getElementById("photoLightbox")?.classList.contains("show")) return
    if (e.key === "ArrowLeft") lbPrev()
    if (e.key === "ArrowRight") lbNext()
  })
}

export default class extends Controller {
  static targets = ["newList", "fileInput"]

  pendingFiles = [] // { file, dataUrl, uid }
  nextUid = 0

  connect() {
    ensureLightbox()
  }

  addFiles(event) {
    Array.from(event.target.files).forEach(file => {
      if (!file.type.startsWith("image/")) return
      const uid = this.nextUid++
      const reader = new FileReader()
      reader.onload = e => {
        this.pendingFiles.push({ file, dataUrl: e.target.result, uid })
        this.renderNewPhotos()
      }
      reader.readAsDataURL(file)
    })
    // Reset so the same file can be selected again
    event.target.value = ""
  }

  renderNewPhotos() {
    this.newListTarget.innerHTML = ""
    this.pendingFiles.forEach(pf => {
      const wrapper = document.createElement("div")
      wrapper.className = "position-relative d-inline-block"
      wrapper.innerHTML = `
        <img src="${pf.dataUrl}"
             class="img-thumbnail"
             style="width:70px;height:70px;object-fit:cover;cursor:pointer;"
             data-photo-src="${pf.dataUrl}"
             data-action="click->acceptance-photos#openLightbox">
        <button type="button"
                class="btn btn-danger position-absolute top-0 end-0 p-0 d-flex align-items-center justify-content-center"
                style="width:20px;height:20px;font-size:12px;border-radius:50%;transform:translate(40%,-40%);line-height:1;"
                data-uid="${pf.uid}"
                data-action="click->acceptance-photos#removeNewPhoto">
          &times;
        </button>
      `
      this.newListTarget.appendChild(wrapper)
    })
    this.syncFileInput()
  }

  removeNewPhoto(event) {
    event.stopPropagation()
    const uid = parseInt(event.currentTarget.dataset.uid)
    this.pendingFiles = this.pendingFiles.filter(pf => pf.uid !== uid)
    this.renderNewPhotos()
  }

  syncFileInput() {
    if (!this.hasFileInputTarget) return
    const dt = new DataTransfer()
    this.pendingFiles.forEach(pf => dt.items.add(pf.file))
    this.fileInputTarget.files = dt.files
  }

  async deleteExistingPhoto(event) {
    event.stopPropagation()
    const wrapper = event.currentTarget.closest("[data-delete-url]")
    if (!wrapper) return
    const url = wrapper.dataset.deleteUrl
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const res = await fetch(url, {
        method: "DELETE",
        headers: { "X-CSRF-Token": csrfToken, "Accept": "application/json" }
      })
      if (res.ok) {
        wrapper.remove()
      }
    } catch (err) {
      console.error("Failed to delete photo", err)
    }
  }

  openLightbox(event) {
    const allImgs = Array.from(this.element.querySelectorAll("[data-photo-src]"))
    const photos = allImgs.map(el => el.dataset.photoSrc)
    const clickedSrc = event.currentTarget.dataset.photoSrc
    const index = Math.max(0, allImgs.findIndex(el => el.dataset.photoSrc === clickedSrc))
    lbShow(photos, index)
  }
}
